import { useRef, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { Spinner } from "../components/Spinner";

export default function Login() {
  const { sendOtp, verifyOtp, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || "/dashboard";
  const [step, setStep] = useState(1); // 1 = phone, 2 = OTP
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loginComplete, setLoginComplete] = useState(false);
  const captchaHostRef = useRef(null);
  const attemptRef = useRef(0);

  // Navigate only after onAuthStateChanged has fired and user is in context.
  // Avoids the race where navigating immediately after verifyOtp leaves user=null
  // in the destination page, causing it to redirect back to login.
  useEffect(() => {
    if (loginComplete && user) {
      navigate(redirectTo, { replace: true });
    }
  }, [loginComplete, user, navigate, redirectTo]);

  useEffect(() => {
    return () => {
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) { /* ignore */ }
        window.recaptchaVerifier = null;
      }
      if (captchaHostRef.current) captchaHostRef.current.innerHTML = "";
    };
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const normalizedPhone = () => {
    const raw = phone.trim().replace(/\s+/g, "");
    if (raw.startsWith("+")) return raw; // already has country code
    const digits = raw.replace(/\D/g, "");
    if (digits.length === 10) return `+91${digits}`;
    if (digits.length === 12 && digits.startsWith("91")) return `+${digits}`;
    return `+${digits}`;
  };

  const doSendOtp = async (p) => {
    setLoading(true);
    try {
      // Use a brand-new container element each attempt so Google's reCAPTCHA
      // library never sees a previously-rendered host element.
      attemptRef.current += 1;
      const containerId = `recaptcha-container-${Date.now()}-${attemptRef.current}`;
      if (captchaHostRef.current) {
        captchaHostRef.current.innerHTML = "";
        const child = document.createElement("div");
        child.id = containerId;
        captchaHostRef.current.appendChild(child);
      }
      const conf = await sendOtp(p, containerId);
      setConfirmation(conf);
      setStep(2);
      setResendCooldown(30);
      toast.success(`OTP sent to ${p}`);
    } catch (err) {
      toast.error("Failed to send OTP: " + (err.message || err.code));
      if (window.recaptchaVerifier) {
        try { window.recaptchaVerifier.clear(); } catch (e) { /* ignore */ }
        window.recaptchaVerifier = null;
      }
      if (captchaHostRef.current) captchaHostRef.current.innerHTML = "";
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    const p = normalizedPhone();
    if (!/^\+\d{8,15}$/.test(p)) {
      toast.error("Enter a valid phone number (e.g. 9XXXXXXXXX)");
      return;
    }
    await doSendOtp(p);
  };

  const handleResendOtp = async () => {
    const p = normalizedPhone();
    setOtp("");
    await doSendOtp(p);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (otp.length < 6) {
      toast.error("Enter the 6-digit OTP");
      return;
    }
    setLoading(true);
    try {
      await verifyOtp(confirmation, otp);
      toast.success("Welcome to Baywash!");
      setLoginComplete(true); // navigate fires in the effect once user is set in context
    } catch (err) {
      toast.error("Invalid OTP: " + (err.message || err.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative border-r border-[#D4D4D8]">
        <img
          src="https://images.unsplash.com/photo-1732357624591-f2137085659b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxjbGVhbiUyMGx1eHVyeSUyMGNhciUyMHN0dWRpb3xlbnwwfHx8fDE3ODE5MzYxMTJ8MA&ixlib=rb-4.1.0&q=85"
          alt="Car wash"
          className="w-full h-full object-cover"
        />
        <div className="absolute top-8 left-8">
          <Link to="/">
            <img src="/logo.png" alt="Baywash" style={{ width: 80, height: "auto", objectFit: "contain" }} />
          </Link>
        </div>
        <div className="absolute bottom-12 left-8 right-8 text-white">
          <div className="font-mono-accent text-xs text-[#FFC000] mb-3">[ SECURE LOGIN ]</div>
          <h2 className="font-display font-black text-4xl tracking-tighter leading-tight">
            One phone.<br />One tap. Drive clean.
          </h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-12">
            <Link to="/">
              <img src="/logo.png" alt="Baywash" style={{ width: 80, height: "auto", objectFit: "contain" }} />
            </Link>
          </div>

          {step === 1 && (
            <>
              <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ STEP 01 / 02 ]</div>
              <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Sign in with phone.</h1>
              <p className="text-sm text-[#525252] mb-10">We&apos;ll text you a one-time code.</p>

              <form onSubmit={handleSendOtp} className="space-y-6" data-testid="phone-form">
                <div>
                  <label className="font-mono-accent text-xs text-[#525252] block mb-2">PHONE NUMBER</label>
                  <Input
                    data-testid="login-phone-input"
                    type="tel"
                    required
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-lg h-12"
                    placeholder="+91 9XXXXXXXXX"
                  />
                  <p className="text-xs text-[#525252] mt-2">Indian numbers auto-prefixed with +91. For others, include country code.</p>
                </div>

                <Button
                  type="submit"
                  data-testid="send-otp-button"
                  disabled={loading}
                  className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wider py-6 text-sm h-auto"
                >
                  {loading ? <Spinner /> : "Send OTP"}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <button
                onClick={() => { setStep(1); setOtp(""); setConfirmation(null); }}
                data-testid="otp-back-button"
                className="flex items-center gap-2 text-xs font-mono-accent text-[#525252] hover:text-[#002FA7] mb-6"
              >
                <ArrowLeft size={14} /> CHANGE NUMBER
              </button>
              <div className="font-mono-accent text-xs text-[#002FA7] mb-3">[ STEP 02 / 02 ]</div>
              <h1 className="font-display font-black text-4xl tracking-tighter mb-2">Enter the code.</h1>
              <p className="text-sm text-[#525252] mb-10">
                Sent to <span className="font-mono">{normalizedPhone()}</span>
              </p>

              <form onSubmit={handleVerifyOtp} className="space-y-6" data-testid="otp-form">
                <div>
                  <label className="font-mono-accent text-xs text-[#525252] block mb-2">6-DIGIT CODE</label>
                  <Input
                    data-testid="login-otp-input"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    required
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                    className="rounded-none border-0 border-b-2 border-[#D4D4D8] focus-visible:ring-0 focus-visible:border-[#002FA7] px-0 text-3xl font-mono tracking-[0.4em] h-14"
                    placeholder="• • • • • •"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="verify-otp-button"
                  disabled={loading}
                  className="w-full bg-[#002FA7] hover:bg-[#001B6B] rounded-none font-bold uppercase tracking-wider py-6 text-sm h-auto"
                >
                  {loading ? <Spinner /> : "Verify & Continue"}
                </Button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    data-testid="resend-otp-button"
                    onClick={handleResendOtp}
                    disabled={loading || resendCooldown > 0}
                    className="font-mono-accent text-xs text-[#525252] hover:text-[#002FA7] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {resendCooldown > 0 ? `RESEND OTP IN ${resendCooldown}s` : "RESEND OTP"}
                  </button>
                </div>
              </form>
            </>
          )}

          <div ref={captchaHostRef} className="mt-6" />
        </div>
      </div>
    </div>
  );
}

